/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDataExportRoutesRequest,
  ListDataExportRoutesSecurity,
} from "../models/operations/listdataexportroutes.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildDataExportRoutesQuery,
  DataExportRoutesQueryData,
  prefetchDataExportRoutes,
  queryKeyDataExportRoutes,
} from "./dataExportRoutes.core.js";
export {
  buildDataExportRoutesQuery,
  type DataExportRoutesQueryData,
  prefetchDataExportRoutes,
  queryKeyDataExportRoutes,
};

export type DataExportRoutesQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listRoutes dataExports
 *
 * @remarks
 * List data source export route configurations for the selected project.
 */
export function useDataExportRoutes(
  request?: ListDataExportRoutesRequest | undefined,
  security?: ListDataExportRoutesSecurity | undefined,
  options?: QueryHookOptions<
    DataExportRoutesQueryData,
    DataExportRoutesQueryError
  >,
): UseQueryResult<DataExportRoutesQueryData, DataExportRoutesQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildDataExportRoutesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listRoutes dataExports
 *
 * @remarks
 * List data source export route configurations for the selected project.
 */
export function useDataExportRoutesSuspense(
  request?: ListDataExportRoutesRequest | undefined,
  security?: ListDataExportRoutesSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    DataExportRoutesQueryData,
    DataExportRoutesQueryError
  >,
): UseSuspenseQueryResult<
  DataExportRoutesQueryData,
  DataExportRoutesQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildDataExportRoutesQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setDataExportRoutesData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: DataExportRoutesQueryData,
): DataExportRoutesQueryData | undefined {
  const key = queryKeyDataExportRoutes(...queryKeyBase);

  return client.setQueryData<DataExportRoutesQueryData>(key, data);
}

export function invalidateDataExportRoutes(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dataExports", "listRoutes", ...queryKeyBase],
  });
}

export function invalidateAllDataExportRoutes(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "dataExports", "listRoutes"],
  });
}
